import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine, ComposedChart,
} from "recharts";
import {
  TrendingUp, TrendingDown, Users, MessageSquare, Clock, AlertTriangle,
  Award, Zap, Globe, Smartphone, Monitor, Tablet, Target, Activity, Download, Sparkles,
} from "lucide-react";

const PALETTE = {
  brand: "#F58220", accent: "#F58220", emerald: "#10B981", amber: "#F59E0B",
  rose: "#DC2657", slate: "#64748B", purple: "#8B5CF6",
};

const ZONE_LABEL = {
  world_class: "World-class", excellent: "Excellent", good: "Good",
  needs_work: "Needs work", critical: "Critical",
};
const ZONE_TONE = {
  world_class: "bg-emerald-500 text-white",
  excellent: "bg-emerald-100 text-emerald-700",
  good: "bg-cyan-100 text-[#1F3A6B]",
  needs_work: "bg-amber-100 text-amber-700",
  critical: "bg-rose-100 text-rose-700",
};

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "trends", label: "Trends & Forecast" },
  { id: "distribution", label: "Distribution" },
  { id: "segments", label: "Segments" },
  { id: "people", label: "People" },
  { id: "voice", label: "Voice of Customer" },
  { id: "operations", label: "Closed-loop Ops" },
  { id: "benchmarks", label: "Benchmarks" },
];

export default function AnalyticsStudioPage() {
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [surveyFilter, setSurveyFilter] = useState("");

  useEffect(() => { api.get("/surveys").then((r) => setSurveys(r.data)); }, []);

  useEffect(() => {
    const qs = new URLSearchParams({ days });
    if (surveyFilter) qs.set("survey_id", surveyFilter);
    api.get(`/analytics/workspace?${qs.toString()}`).then((r) => setData(r.data));
  }, [days, surveyFilter]);

  if (!data) return <div className="text-slate-500">Computing analytics…</div>;

  const t = data.totals;
  const k = data.kpis;

  return (
    <div className="space-y-5 fade-in">
      {/* Filter bar */}
      <Card className="p-4 border-slate-100">
        <div className="flex flex-wrap items-center gap-3">
          <Globe size={16} className="text-[#F58220]"/>
          <p className="text-sm font-semibold text-[#1F3A6B]">Workspace analytics</p>
          <select
            data-testid="analytics-days"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-9 text-sm border border-slate-200 rounded-md px-2 bg-white ml-auto"
          >
            {[7, 30, 90, 180, 365].map((d) => <option key={d} value={d}>Last {d} days</option>)}
          </select>
          <select
            data-testid="analytics-survey"
            value={surveyFilter}
            onChange={(e) => setSurveyFilter(e.target.value)}
            className="h-9 text-sm border border-slate-200 rounded-md px-2 bg-white max-w-[240px]"
          >
            <option value="">All surveys</option>
            {surveys.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
          <Link to="/app/reports"><Button size="sm" variant="outline"><Download size={14} className="mr-1.5"/>Reports & CSV</Button></Link>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto no-scrollbar border-b border-slate-100">
        {TABS.map((tb) => (
          <button
            key={tb.id}
            data-testid={`analytics-tab-${tb.id}`}
            onClick={() => setTab(tb.id)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition ${
              tab === tb.id ? "border-[#F58220] text-[#1F3A6B]" : "border-transparent text-slate-500 hover:text-[#1F3A6B]"
            }`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab data={data}/>}
      {tab === "trends" && <TrendsTab data={data}/>}
      {tab === "distribution" && <DistributionTab data={data}/>}
      {tab === "segments" && <SegmentsTab data={data}/>}
      {tab === "people" && <PeopleTab data={data}/>}
      {tab === "voice" && <VoiceTab data={data}/>}
      {tab === "operations" && <OperationsTab data={data}/>}
      {tab === "benchmarks" && <BenchmarksTab data={data}/>}
    </div>
  );
}

/* ========================= OVERVIEW TAB ========================= */
function OverviewTab({ data }) {
  const t = data.totals, k = data.kpis, b = data.benchmark;
  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-6 gap-3">
        <Kpi data-testid="kpi-nps-score" label="NPS Score" value={t.nps_score} tone="brand" trend={k.delta} icon={<Award size={16}/>}/>
        <Kpi label="Responses" value={t.responses} tone="cyan" sub={`${t.responses_all_time} all-time`} icon={<Activity size={16}/>}/>
        <Kpi label="Promoters" value={`${pct(t.promoters, t.promoters + t.passives + t.detractors)}%`} tone="emerald" sub={`${t.promoters} ppl`} icon={<TrendingUp size={16}/>}/>
        <Kpi label="Passives" value={`${pct(t.passives, t.promoters + t.passives + t.detractors)}%`} tone="amber" sub={`${t.passives} ppl`} icon={<Users size={16}/>}/>
        <Kpi label="Detractors" value={`${pct(t.detractors, t.promoters + t.passives + t.detractors)}%`} tone="rose" sub={`${t.detractors} ppl`} icon={<TrendingDown size={16}/>}/>
        <Kpi label="Zone" value={ZONE_LABEL[b.zone]} tone="brand" sub={`Industry: ${b.industry_avg}`} icon={<Target size={16}/>}/>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5 border-slate-100">
          <CardHead title="NPS trend" sub={`Last ${data.period_days} days · ${k.delta >= 0 ? "+" : ""}${k.delta} vs prior 7d`}/>
          <div className="h-72 mt-3">
            {data.trend.length === 0 ? <Empty/> : (
              <ResponsiveContainer>
                <AreaChart data={data.trend.map((d) => ({ ...d, label: d.date.slice(5) }))}>
                  <defs><linearGradient id="gt" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={PALETTE.accent} stopOpacity="0.4"/><stop offset="1" stopColor={PALETTE.accent} stopOpacity="0"/></linearGradient></defs>
                  <CartesianGrid stroke="#eef2f7" vertical={false}/>
                  <XAxis dataKey="label" tickLine={false} axisLine={false}/>
                  <YAxis domain={[-100, 100]} tickLine={false} axisLine={false} width={32}/>
                  <ReferenceLine y={0} stroke="#cbd5e1" strokeDasharray="3 3"/>
                  <ReferenceLine y={50} stroke={PALETTE.emerald} strokeDasharray="3 3" label={{ value: "Excellent", fill: PALETTE.emerald, fontSize: 10, position: "right" }}/>
                  <Tooltip/>
                  <Area type="monotone" dataKey="nps" stroke={PALETTE.accent} strokeWidth={2.4} fill="url(#gt)"/>
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="p-5 border-slate-100">
          <CardHead title="Loyalty mix" sub="Promoters · Passives · Detractors"/>
          <div className="h-56 mt-3">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={[
                  { name: "Promoters", value: t.promoters, color: PALETTE.emerald },
                  { name: "Passives", value: t.passives, color: PALETTE.amber },
                  { name: "Detractors", value: t.detractors, color: PALETTE.rose },
                ]} dataKey="value" innerRadius={52} outerRadius={78} paddingAngle={2}>
                  {["emerald","amber","rose"].map((c, i) => <Cell key={i} fill={PALETTE[c]}/>)}
                </Pie>
                <Tooltip/>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <LegendItem color={PALETTE.emerald} label="Promoters" value={t.promoters}/>
            <LegendItem color={PALETTE.amber} label="Passives" value={t.passives}/>
            <LegendItem color={PALETTE.rose} label="Detractors" value={t.detractors}/>
          </div>
        </Card>
      </div>

      <div className="grid md:grid-cols-4 gap-3">
        <Kpi label="Median score" value={t.median_score} tone="brand" icon={<Activity size={16}/>}/>
        <Kpi label="Avg score" value={t.avg_score} tone="brand" icon={<Activity size={16}/>}/>
        <Kpi label="Std deviation" value={t.std_dev} tone="brand" sub="Spread of responses"/>
        <Kpi label="Identified share" value={`${pct(t.identified, t.identified + t.anonymous)}%`} tone="cyan" sub={`${t.identified}/${t.identified + t.anonymous}`}/>
      </div>

      {data.anomalies.length > 0 && (
        <Card className="p-5 border-rose-100 bg-rose-50/40">
          <CardHead title="Anomalies detected (z ≥ 2.0)" icon={<AlertTriangle size={16} className="text-rose-600"/>}/>
          <ul className="mt-3 grid md:grid-cols-2 gap-2">
            {data.anomalies.map((a) => (
              <li key={a.date} className={`flex justify-between items-center text-sm border rounded-md px-3 py-2 ${a.direction === "drop" ? "bg-rose-50 border-rose-100 text-rose-700" : "bg-emerald-50 border-emerald-100 text-emerald-700"}`}>
                <span className="font-semibold">{a.date}</span>
                <span>NPS <strong className="tabular-nums">{a.nps}</strong> · {a.responses} resp · z={a.z}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

/* ========================= TRENDS TAB ========================= */
function TrendsTab({ data }) {
  const merged = data.trend.map((t, i) => ({
    ...t,
    label: t.date.slice(5),
    r7: data.rolling7[i]?.nps,
    r30: data.rolling30[i]?.nps,
  }));
  const forecastMerged = [
    ...merged.slice(-7),
    ...data.forecast.map((f) => ({ label: f.date.slice(5), nps: null, forecast_nps: f.nps })),
  ];
  return (
    <div className="space-y-5">
      <Card className="p-5 border-slate-100">
        <CardHead title="NPS with 7-day & 30-day rolling averages" sub="Smooths out daily noise — industry standard"/>
        <div className="h-80 mt-3">
          {merged.length === 0 ? <Empty/> : (
            <ResponsiveContainer>
              <ComposedChart data={merged}>
                <CartesianGrid stroke="#eef2f7" vertical={false}/>
                <XAxis dataKey="label" tickLine={false} axisLine={false}/>
                <YAxis domain={[-100, 100]} tickLine={false} axisLine={false} width={32}/>
                <Tooltip/><Legend/>
                <Bar dataKey="responses" fill="#e0f2fe" yAxisId="r"/>
                <YAxis yAxisId="r" orientation="right" width={28} axisLine={false} tickLine={false}/>
                <Line type="monotone" dataKey="nps" stroke={PALETTE.slate} strokeWidth={1.2} dot={false} name="Daily NPS"/>
                <Line type="monotone" dataKey="r7" stroke={PALETTE.accent} strokeWidth={2.6} dot={false} name="7-day avg"/>
                <Line type="monotone" dataKey="r30" stroke={PALETTE.brand} strokeWidth={2.6} strokeDasharray="4 2" dot={false} name="30-day avg"/>
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 border-slate-100">
          <CardHead title="7-day predictive forecast" sub="Linear-regression projection (indicative)" icon={<Zap size={16} className="text-[#F58220]"/>}/>
          <div className="h-64 mt-3">
            {forecastMerged.length === 0 ? <Empty/> : (
              <ResponsiveContainer>
                <LineChart data={forecastMerged}>
                  <CartesianGrid stroke="#eef2f7" vertical={false}/>
                  <XAxis dataKey="label" tickLine={false} axisLine={false}/>
                  <YAxis domain={[-100, 100]} tickLine={false} axisLine={false} width={32}/>
                  <Tooltip/><Legend/>
                  <Line type="monotone" dataKey="nps" stroke={PALETTE.brand} strokeWidth={2} dot name="Actual"/>
                  <Line type="monotone" dataKey="forecast_nps" stroke={PALETTE.accent} strokeWidth={2.4} strokeDasharray="5 3" dot name="Forecast"/>
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="p-5 border-slate-100">
          <CardHead title="Daily response volume"/>
          <div className="h-64 mt-3">
            {data.trend.length === 0 ? <Empty/> : (
              <ResponsiveContainer>
                <BarChart data={data.trend.map((t) => ({ ...t, label: t.date.slice(5) }))}>
                  <CartesianGrid stroke="#eef2f7" vertical={false}/>
                  <XAxis dataKey="label" tickLine={false} axisLine={false}/>
                  <YAxis tickLine={false} axisLine={false} width={28}/>
                  <Tooltip/>
                  <Bar dataKey="promoters" stackId="a" fill={PALETTE.emerald}/>
                  <Bar dataKey="passives" stackId="a" fill={PALETTE.amber}/>
                  <Bar dataKey="detractors" stackId="a" fill={PALETTE.rose}/>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ========================= DISTRIBUTION TAB ========================= */
function DistributionTab({ data }) {
  const dist = data.distribution.map((d) => ({
    ...d,
    color: d.score >= 9 ? PALETTE.emerald : d.score >= 7 ? PALETTE.amber : PALETTE.rose,
  }));
  return (
    <div className="space-y-5">
      <Card className="p-5 border-slate-100">
        <CardHead title="Score distribution (0–10)" sub="Promoter (9–10) · Passive (7–8) · Detractor (0–6)"/>
        <div className="h-80 mt-3">
          <ResponsiveContainer>
            <BarChart data={dist}>
              <CartesianGrid stroke="#eef2f7" vertical={false}/>
              <XAxis dataKey="score" tickLine={false} axisLine={false}/>
              <YAxis tickLine={false} axisLine={false} width={28}/>
              <Tooltip/>
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {dist.map((d, i) => <Cell key={i} fill={d.color}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-5 border-slate-100">
        <CardHead title="Weighted scores per survey" sub="Surveys with question weights assigned"/>
        {data.weighted_scores.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">Assign weights to questions in a survey to see weighted scoring here.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {data.weighted_scores.map((w, i) => (
              <li key={i} className="flex items-center gap-3 border border-slate-100 rounded-md px-3 py-2">
                <span className="flex-1 text-sm font-semibold text-[#1F3A6B]">{w.survey}</span>
                <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#1F3A6B] to-[#F58220]" style={{ width: `${w.score}%` }}/>
                </div>
                <span className="text-sm font-bold tabular-nums">{w.score}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/* ========================= SEGMENTS TAB ========================= */
function SegmentsTab({ data }) {
  return (
    <div className="space-y-5">
      <div className="grid lg:grid-cols-2 gap-4">
        <SegmentCard title="NPS by survey" rows={data.segments.by_survey}/>
        <SegmentCard title="NPS by country" rows={data.segments.by_country}/>
        <SegmentCard title="NPS by source / campaign" rows={data.segments.by_source}/>
        <SegmentCard title="NPS by device" rows={data.segments.by_device} icons={{ mobile: <Smartphone size={14}/>, desktop: <Monitor size={14}/>, tablet: <Tablet size={14}/> }}/>
      </div>

      <Card className="p-5 border-slate-100">
        <CardHead title="Identified vs anonymous" sub="Higher identified share improves closed-loop recovery"/>
        <div className="h-56 mt-3">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={data.segments.identified_vs_anonymous} dataKey="count" innerRadius={55} outerRadius={85} paddingAngle={2}>
                <Cell fill={PALETTE.accent}/><Cell fill={PALETTE.slate}/>
              </Pie>
              <Tooltip/><Legend/>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}

function SegmentCard({ title, rows, icons }) {
  const top = rows.slice(0, 8);
  return (
    <Card className="p-5 border-slate-100">
      <CardHead title={title}/>
      <div className="h-64 mt-3">
        {top.length === 0 ? <Empty/> : (
          <ResponsiveContainer>
            <BarChart data={top} layout="vertical" margin={{ left: 8 }}>
              <CartesianGrid stroke="#eef2f7" horizontal={false}/>
              <XAxis type="number" domain={[-100, 100]} tickLine={false} axisLine={false}/>
              <YAxis type="category" dataKey="name" tickLine={false} axisLine={false} width={110}/>
              <Tooltip/>
              <Bar dataKey="nps" radius={[0, 4, 4, 0]}>
                {top.map((r, i) => {
                  const fill = r.nps >= 50 ? PALETTE.emerald : r.nps >= 0 ? PALETTE.accent : PALETTE.rose;
                  return <Cell key={i} fill={fill}/>;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}

/* ========================= PEOPLE TAB ========================= */
function PeopleTab({ data }) {
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <PeopleList title="Top promoters" tone="emerald" items={data.promoters}/>
      <PeopleList title="Top detractors" tone="rose" items={data.detractors}/>
    </div>
  );
}
function PeopleList({ title, tone, items }) {
  return (
    <Card className="p-5 border-slate-100">
      <CardHead title={title} sub={`${items.length} most recent`}/>
      {items.length === 0 ? <p className="mt-3 text-sm text-slate-500">No {title.toLowerCase()} yet.</p> : (
        <ul className="mt-3 space-y-2 max-h-[520px] overflow-auto pr-1">
          {items.map((p) => (
            <li key={p.id} data-testid={`person-${p.id}`} className={`border rounded-md p-3 ${tone === "emerald" ? "border-emerald-100 bg-emerald-50/40" : "border-rose-100 bg-rose-50/40"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-[#1F3A6B]">{p.name}</p>
                  <p className="text-xs text-slate-500">{p.email || "anonymous"} · {p.survey}</p>
                </div>
                <span className={`text-lg font-extrabold tabular-nums ${tone === "emerald" ? "text-emerald-600" : "text-rose-600"}`}>{p.score}</span>
              </div>
              {p.comment && <p className="mt-2 text-sm text-slate-700 border-t border-slate-100 pt-2 line-clamp-3">"{p.comment}"</p>}
              <p className="mt-2 text-[10px] text-slate-400">{new Date(p.date).toLocaleString()}</p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* ========================= VOICE OF CUSTOMER TAB ========================= */
function VoiceTab({ data }) {
  return (
    <div className="space-y-4">
      <Card className="p-5 border-[#FFE1BE] bg-[#FFF0DF]/30">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-[#F58220] text-white grid place-items-center"><Sparkles size={18}/></div>
          <div className="flex-1">
            <p className="text-sm font-bold text-[#1F3A6B]">AI-ready insights</p>
            <p className="text-sm text-slate-600">Head to <Link to="/app/ai" className="text-[#F58220] font-semibold">AI Insights</Link> to chat with GPT-5.2 about these themes — ask root cause, compare periods, draft a closed-loop reply.</p>
          </div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 border-slate-100">
          <CardHead title="Top themes (overall)" sub="Frequency of keywords in open comments"/>
          <WordCloud items={data.themes.overall} tone="cyan"/>
        </Card>
        <Card className="p-5 border-rose-100 bg-rose-50/20">
          <CardHead title="Top themes (detractors)" sub="What unhappy customers mention most" icon={<AlertTriangle size={16} className="text-rose-600"/>}/>
          <WordCloud items={data.themes.detractor} tone="rose"/>
        </Card>
      </div>
    </div>
  );
}

function WordCloud({ items, tone }) {
  if (!items?.length) return <p className="mt-3 text-sm text-slate-500">Not enough text responses yet.</p>;
  const max = items[0]?.count || 1;
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      {items.map((t, i) => {
        const scale = 0.8 + (t.count / max) * 1.3;
        const bg = tone === "rose" ? "bg-rose-100 text-rose-700" : "bg-[#FFF0DF] text-[#1F3A6B]";
        return (
          <span key={i} className={`${bg} rounded-full px-3 py-1 font-semibold`} style={{ fontSize: `${scale}rem` }}>
            {t.theme} <span className="opacity-60 text-xs">·{t.count}</span>
          </span>
        );
      })}
    </div>
  );
}

/* ========================= OPERATIONS TAB ========================= */
function OperationsTab({ data }) {
  const t = data.tickets;
  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-4 gap-3">
        <Kpi label="Open tickets" value={t.open} tone="rose" icon={<AlertTriangle size={16}/>}/>
        <Kpi label="In progress" value={t.in_progress} tone="amber" icon={<Clock size={16}/>}/>
        <Kpi label="Resolved" value={t.resolved} tone="emerald" sub={`${t.resolution_rate}% rate`}/>
        <Kpi label="Avg resolution" value={`${t.avg_resolution_hours}h`} tone="brand"/>
      </div>

      <Card className="p-5 border-slate-100">
        <CardHead title="Closed-loop funnel"/>
        <div className="h-60 mt-3">
          <ResponsiveContainer>
            <BarChart data={[
              { stage: "Open", count: t.open, color: PALETTE.rose },
              { stage: "In progress", count: t.in_progress, color: PALETTE.amber },
              { stage: "Resolved", count: t.resolved, color: PALETTE.emerald },
              { stage: "Escalated", count: t.escalated, color: PALETTE.purple },
            ]}>
              <CartesianGrid stroke="#eef2f7" vertical={false}/>
              <XAxis dataKey="stage" tickLine={false} axisLine={false}/>
              <YAxis tickLine={false} axisLine={false} width={28}/>
              <Tooltip/>
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {[PALETTE.rose, PALETTE.amber, PALETTE.emerald, PALETTE.purple].map((c, i) => <Cell key={i} fill={c}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <Link to="/app/tickets"><Button size="sm" variant="outline" className="mt-4"><MessageSquare size={14} className="mr-1.5"/>Open ticket queue</Button></Link>
      </Card>
    </div>
  );
}

/* ========================= BENCHMARKS TAB ========================= */
function BenchmarksTab({ data }) {
  const b = data.benchmark;
  const pctVal = Math.max(0, Math.min(100, (b.score + 100) / 2)); // map -100..100 to 0..100
  return (
    <div className="space-y-5">
      <Card className="p-6 border-slate-100">
        <CardHead title="Industry benchmark" sub={`Your workspace vs "${b.industry}" benchmark`}/>
        <div className="mt-4 grid md:grid-cols-3 gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Your NPS</p>
            <p className="text-5xl font-extrabold text-[#1F3A6B] tabular-nums">{b.score}</p>
            <Badge className={`${ZONE_TONE[b.zone]} mt-2`}>{ZONE_LABEL[b.zone]}</Badge>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Industry avg</p>
            <p className="text-5xl font-extrabold text-slate-500 tabular-nums">{b.industry_avg}</p>
            <p className="mt-2 text-xs text-slate-500 capitalize">{b.industry}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Δ vs industry</p>
            <p className={`text-5xl font-extrabold tabular-nums ${b.delta_vs_industry >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
              {b.delta_vs_industry >= 0 ? "+" : ""}{b.delta_vs_industry}
            </p>
            <p className="mt-2 text-xs text-slate-500">{b.delta_vs_industry >= 0 ? "Ahead of peers" : "Behind peers"}</p>
          </div>
        </div>

        <div className="mt-6">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">NPS zone</p>
          <div className="relative h-8 rounded-full bg-gradient-to-r from-rose-500 via-amber-400 via-cyan-400 to-emerald-500 overflow-hidden">
            <div className="absolute top-0 bottom-0 bg-white/80 rounded-full" style={{ left: `calc(${pctVal}% - 2px)`, width: "4px" }}/>
          </div>
          <div className="mt-1 grid grid-cols-5 text-[10px] uppercase font-bold text-slate-500">
            <span>Critical</span><span className="text-center">Needs work</span><span className="text-center">Good</span><span className="text-center">Excellent</span><span className="text-right">World-class</span>
          </div>
        </div>
      </Card>

      <Card className="p-5 border-slate-100">
        <CardHead title="NPS benchmarks by industry (Bain & Co.)" sub="Median NPS reported across major published benchmark studies"/>
        <ul className="mt-3 grid md:grid-cols-3 gap-2 text-sm">
          {[
            ["SaaS", 30], ["Retail", 42], ["E-commerce", 45], ["Hospitality", 52],
            ["Banking", 25], ["Healthcare", 36], ["Telecom", 22], ["Education", 40],
            ["Automotive", 35], ["Logistics", 30], ["Real estate", 28], ["Fitness & wellness", 45],
          ].map(([k, v]) => (
            <li key={k} className={`flex items-center justify-between border rounded-md px-3 py-2 ${k.toLowerCase() === b.industry.toLowerCase() ? "border-cyan-200 bg-[#FFF0DF]" : "border-slate-100"}`}>
              <span className="text-slate-700">{k}</span>
              <span className="font-extrabold tabular-nums text-[#1F3A6B]">{v}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

/* ========================= PRIMITIVES ========================= */
function Kpi({ label, value, tone, sub, icon, trend, ...rest }) {
  const map = { brand: "text-[#1F3A6B]", cyan: "text-[#F58220]", emerald: "text-emerald-600",
                amber: "text-amber-600", rose: "text-rose-600" };
  return (
    <Card {...rest} className="p-4 border-slate-100">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
        {icon && <span className="text-slate-400">{icon}</span>}
      </div>
      <p className={`mt-1 text-3xl font-extrabold tabular-nums ${map[tone]}`}>{value}</p>
      {sub !== undefined && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
      {trend !== undefined && trend !== null && (
        <p className={`mt-1 text-[11px] font-bold ${trend >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{trend >= 0 ? "▲" : "▼"} {Math.abs(trend)} pts</p>
      )}
    </Card>
  );
}
function CardHead({ title, sub, icon }) {
  return (
    <div className="flex items-start gap-2">
      {icon}
      <div>
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{title}</p>
        {sub && <p className="text-[11px] text-slate-500">{sub}</p>}
      </div>
    </div>
  );
}
function LegendItem({ color, label, value }) {
  return (
    <div className="flex items-center gap-1.5 text-slate-700">
      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color }}/>
      <span>{label}</span>
      <span className="ml-auto font-bold tabular-nums">{value}</span>
    </div>
  );
}
function Empty() {
  return <div className="h-full grid place-items-center text-sm text-slate-500">No data for this period yet.</div>;
}
function pct(n, d) { return d ? Math.round((n / d) * 100) : 0; }
